// Pure helpers shared between edge functions and Vitest unit tests. Kept
// free of Deno-specific imports so Vitest can import this file directly via
// a relative path. Mirrors the client-side implementation in
// `src/lib/recurrence.ts` — keep them in sync if either changes.

export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly";

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  dayOfWeek: number | null; // 0=Sunday..6=Saturday
  endDate: string | null; // YYYY-MM-DD
}

export interface TaskWithRecurrence {
  recurrence_frequency: string | null;
  recurrence_interval: number | null;
  recurrence_day_of_week: number | null;
  recurrence_end_date: string | null;
}

export function ruleFromTask(task: TaskWithRecurrence): RecurrenceRule | null {
  const frequency = task.recurrence_frequency;
  if (
    frequency !== "daily" &&
    frequency !== "weekly" &&
    frequency !== "monthly" &&
    frequency !== "yearly"
  ) {
    return null;
  }
  return {
    frequency,
    interval: task.recurrence_interval ?? 1,
    dayOfWeek: task.recurrence_day_of_week,
    endDate: task.recurrence_end_date,
  };
}

function toUtc(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function formatUtcDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Adds months, clamping to the last day when the target month is short. */
function addMonthsUtc(date: Date, months: number, preferredDay: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(preferredDay, lastDay)));
}

/**
 * Next occurrence of a repeating task, as a plain calendar date.
 *
 * `today` is passed in rather than read from the clock: the web app asks the
 * person's machine, and the connector uses the date in the person's own
 * timezone. An edge function's clock is UTC, and "have I passed today yet"
 * answered in the wrong zone rolls a daily task forward a day too early or
 * too late.
 */
export function nextOccurrence(
  currentDate: string,
  rule: RecurrenceRule,
  today: string,
): string | null {
  const floor = toUtc(today);
  let next = toUtc(currentDate);
  const interval = Math.max(1, rule.interval || 1);

  switch (rule.frequency) {
    case "daily":
      do {
        next = new Date(next.getTime() + interval * 86_400_000);
      } while (next < floor);
      break;

    case "weekly": {
      const targetDay = rule.dayOfWeek ?? next.getUTCDay();
      next = new Date(next.getTime() + interval * 7 * 86_400_000);
      next = new Date(
        next.getTime() + (targetDay - next.getUTCDay()) * 86_400_000,
      );
      while (next < floor) {
        next = new Date(next.getTime() + interval * 7 * 86_400_000);
      }
      break;
    }

    case "monthly": {
      const preferredDay = next.getUTCDate();
      do {
        next = addMonthsUtc(next, interval, preferredDay);
      } while (next < floor);
      break;
    }

    case "yearly": {
      const month = next.getUTCMonth();
      const day = next.getUTCDate();
      do {
        next = addMonthsUtc(next, interval * 12, day);
        // Clamp keeps 29 February on a non-leap year at 28 February.
        next = new Date(
          Date.UTC(
            next.getUTCFullYear(),
            month,
            Math.min(
              day,
              new Date(Date.UTC(next.getUTCFullYear(), month + 1, 0)).getUTCDate(),
            ),
          ),
        );
      } while (next < floor);
      break;
    }
  }

  const result = formatUtcDate(next);
  return rule.endDate && result > rule.endDate ? null : result;
}

const BYDAY_CODES = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

const PRIORITY_EMOJI: Record<number, string> = {
  1: "🔴", // 🔴 high
  2: "🟠", // 🟠 medium
  3: "🟡", // 🟡 low
};

// Builds the Google Calendar event title: optional ✅ (completed) then optional
// priority emoji then the task title. e.g. "✅ 🔴 Buy groceries".
export function buildEventSummary(
  title: string | undefined,
  priority: number | null | undefined,
  completed: boolean | undefined,
): string {
  const base = title || "Untitled task";
  const emoji = priority != null ? PRIORITY_EMOJI[priority] : undefined;
  const withPriority = emoji ? `${emoji} ${base}` : base;
  return completed ? `✅ ${withPriority}` : withPriority;
}

export function buildRRule(rule: RecurrenceRule): string {
  const parts: string[] = [`FREQ=${rule.frequency.toUpperCase()}`];

  if (rule.interval > 1) {
    parts.push(`INTERVAL=${rule.interval}`);
  }

  if (rule.frequency === "weekly" && rule.dayOfWeek != null) {
    parts.push(`BYDAY=${BYDAY_CODES[rule.dayOfWeek]}`);
  }

  if (rule.endDate) {
    const compact = rule.endDate.replace(/-/g, "");
    parts.push(`UNTIL=${compact}T235959Z`);
  }

  return `RRULE:${parts.join(";")}`;
}

export type CalendarLinkEntity = "task" | "subtask";

// Sign-in is stored for www.usejudi.com. The short host is a separate site,
// and a saved copy of the app there shows the login page. Calendar links
// must use the www host even when APP_URL is the short host.
function appUrlForCalendarLink(appUrl: string): string {
  let url: URL;
  try {
    url = new URL(appUrl);
  } catch {
    return appUrl;
  }
  if (url.hostname === "usejudi.com") {
    url.hostname = "www.usejudi.com";
  }
  return url.toString().replace(/\/$/, "");
}

/**
 * Deep-link URL embedded in a Google Calendar event's description. Subtasks
 * link to their parent task page — there's no subtask-specific route — with
 * a `subtask` query param so the app opens straight to that subtask instead
 * of the parent task itself. Shared by every edge function that builds a
 * Google Calendar event body (`google-calendar-sync`,
 * `reschedule-persistent-tasks`, and the initial-sync path below).
 */
export function deepLinkFor(
  appUrl: string | undefined,
  entity: CalendarLinkEntity,
  taskId: string,
  parentTaskId?: string | null,
): string {
  const base = appUrl === undefined ? appUrl : appUrlForCalendarLink(appUrl);
  if (entity === "subtask" && parentTaskId) {
    return `${base}/task/${parentTaskId}?subtask=${taskId}`;
  }
  return `${base}/task/${taskId}`;
}

export interface InitialSyncTask extends TaskWithRecurrence {
  id: string;
  title: string;
  scheduled_date: string;
  scheduled_time: string | null;
  duration: number | null;
  completed: boolean;
  priority?: number | null;
}

export interface InitialSyncLink {
  entity: CalendarLinkEntity;
  parentTaskId?: string | null;
}

/**
 * A step is copied on first connect when its date is today or later.
 * Steps do not repeat, so a past date stays off the new calendar until
 * the step is saved again. That matches a one-off task.
 */
export function stepJoinsInitialSync(
  step: { scheduled_date: string | null },
  today: string,
): boolean {
  return step.scheduled_date != null && step.scheduled_date >= today;
}

/**
 * Build the Google Calendar event body for the one-shot initial sync that
 * runs when a user first connects their calendar. Recurring tasks emit an
 * RRULE so Google generates future occurrences — without it, recurring tasks
 * silently flatten into single one-off events.
 *
 * `timezone` is the IANA zone the wall-clock `scheduled_time` should be
 * interpreted in. Pass the user's account timezone (from
 * `users/me/settings/timezone`) so events land at the hour the user typed in
 * JuDI rather than at that hour in UTC.
 */
export function buildInitialSyncEventBody(
  task: InitialSyncTask,
  appUrl: string,
  timezone: string,
  link: InitialSyncLink = { entity: "task" },
): Record<string, unknown> {
  const eventBody: Record<string, unknown> = {
    summary: buildEventSummary(task.title, task.priority, task.completed),
    description: `View in JuDI: ${
      deepLinkFor(appUrl, link.entity, task.id, link.parentTaskId)
    }`,
  };

  if (task.scheduled_time) {
    // PostgreSQL TIME returns "HH:MM:SS"; frontend may send "HH:MM".
    const timeStr = task.scheduled_time;
    const timeWithSeconds = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const durationMinutes = task.duration || 30;
    // Wall-clock arithmetic via UTC: parsing with `Z` lets us add the duration
    // and pull "YYYY-MM-DD" + "HH:MM:SS" back out without DST/local-time
    // interference. The emitted dateTime strings are naive — `timeZone` tells
    // Google how to interpret them. Using the computed end date (rather than
    // `scheduled_date`) keeps the event valid when duration crosses midnight.
    const startDate = new Date(`${task.scheduled_date}T${timeWithSeconds}Z`);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
    const endDateStr = endDate.toISOString().slice(0, 10);
    const endTime = endDate.toISOString().slice(11, 19);
    eventBody.start = {
      dateTime: `${task.scheduled_date}T${timeWithSeconds}`,
      timeZone: timezone,
    };
    eventBody.end = {
      dateTime: `${endDateStr}T${endTime}`,
      timeZone: timezone,
    };
  } else {
    eventBody.start = { date: task.scheduled_date };
    eventBody.end = { date: task.scheduled_date };
  }

  const rule = ruleFromTask(task);
  if (rule) {
    eventBody.recurrence = [buildRRule(rule)];
  }

  return eventBody;
}
